import React, { useState } from 'react';
import { httpsCallable, getFunctions } from 'firebase/functions';
import toast from 'react-hot-toast';
import { app } from '../../lib/firebase';
import { Task } from '../../types/task';
import Modal from '../common/Modal';
import ConfirmDialog from '../common/ConfirmDialog';
import { describeRecurrence } from '../../lib/recurrence';
import { ArrowPathIcon, PencilIcon, TrashIcon, PlayIcon, PauseIcon, PlusIcon, ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  templates: Task[];
  onEdit: (t: Task) => void;
  onNew: () => void;
  onToggleActive: (t: Task, active: boolean) => Promise<void> | void;
  onDelete: (id: string) => Promise<void> | void;
}

/** Ismétlődő feladat-sablonok kezelése. */
const RecurringTasksModal: React.FC<Props> = ({ isOpen, onClose, templates, onEdit, onNew, onToggleActive, onDelete }) => {
  const [busy, setBusy] = useState<string | null>(null);
  const [del, setDel] = useState<Task | null>(null);

  const runNow = async (t: Task) => {
    try {
      setBusy(t.id);
      const fn = httpsCallable<{ templateId: string }, { created: { taskNumber: string; worksheetId?: string | null }[] }>(getFunctions(app, 'europe-west1'), 'recurringTaskRunNow');
      const res = await fn({ templateId: t.id });
      const c = res.data?.created?.[0];
      toast.success(c ? `Létrehozva: ${c.taskNumber}${c.worksheetId ? ' + munkalap' : ''}` : 'Nem jött létre példány');
    } catch (e: any) {
      toast.error(e?.message?.replace(/^.*?:\s*/, '') || 'Nem sikerült');
    } finally { setBusy(null); }
  };

  const sorted = templates.slice().sort((a, b) => (a.recurrence?.active === b.recurrence?.active ? (a.recurrence?.nextRunAt?.getTime() || 0) - (b.recurrence?.nextRunAt?.getTime() || 0) : a.recurrence?.active ? -1 : 1));

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Ismétlődő feladatok" size="3xl">
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <p className="text-xs text-gray-500 dark:text-gray-400">Karbantartási ciklusok, visszatérő ellenőrzések, rendszeres riportok. A sablonból az ütem szerint automatikusan létrejön a feladat (és kérésre a munkalap-vázlat), a beállított előkészítési idővel a határidő előtt. A napi futás reggel 5:20-kor van.</p>
          <button type="button" onClick={onNew} className="crm-btn crm-btn-primary h-8 text-xs flex-shrink-0"><PlusIcon className="w-4 h-4" />Új sablon</button>
        </div>

        {sorted.length === 0 ? (
          <div className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">
            <ArrowPathIcon className="w-8 h-8 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
            Még nincs ismétlődő feladat. Új feladatnál a második fülön kapcsold be az <b>Ismétlődés</b>t.
          </div>
        ) : (
          <div className="crm-card overflow-hidden divide-y divide-[var(--crm-border)]">
            {sorted.map(t => {
              const r = t.recurrence!;
              const active = r.active !== false;
              return (
                <div key={t.id} className={`flex items-center gap-3 px-3 py-2 ${active ? '' : 'opacity-60'}`}>
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${active ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'}`} title={active ? 'Aktív' : 'Szünetel'} />
                  <div className="min-w-0 flex-1">
                    <button type="button" onClick={() => onEdit(t)} className="block truncate text-left text-sm font-medium text-gray-900 dark:text-gray-100 hover:text-primary-600 hover:underline max-w-full">{t.title}</button>
                    <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                      {describeRecurrence(r)}
                      {r.nextRunAt ? ` · következő: ${r.nextRunAt.toLocaleDateString('hu-HU')}` : ''}
                      {r.leadDays ? ` (${r.leadDays} nappal előbb jön létre)` : ''}
                      {t.customerName ? ` · ${t.customerName}` : ''}
                      {t.assignedToName ? ` · ${t.assignedToName}` : ''}
                      {r.createWorksheet ? ' · munkalappal' : ''}
                      {r.count ? ` · eddig ${r.count}×` : ''}
                      {r.endDate ? ` · vége ${r.endDate.toLocaleDateString('hu-HU')}` : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    <button type="button" onClick={() => runNow(t)} disabled={busy === t.id} className="w-8 h-8 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-primary-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50" title="Példány létrehozása most"><ClipboardDocumentCheckIcon className="w-4 h-4" /></button>
                    <button type="button" onClick={() => onToggleActive(t, !active)} className="w-8 h-8 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700" title={active ? 'Szüneteltetés' : 'Folytatás'}>{active ? <PauseIcon className="w-4 h-4" /> : <PlayIcon className="w-4 h-4" />}</button>
                    <button type="button" onClick={() => onEdit(t)} className="w-8 h-8 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700" title="Szerkesztés"><PencilIcon className="w-4 h-4" /></button>
                    <button type="button" onClick={() => setDel(t)} className="w-8 h-8 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20" title="Törlés"><TrashIcon className="w-4 h-4" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <ConfirmDialog
        isOpen={!!del}
        title="Sablon törlése"
        message={`Törlöd a(z) „${del?.title}” ismétlődő sablont? A már létrejött feladatok megmaradnak.`}
        confirmText="Törlés"
        cancelText="Mégse"
        type="danger"
        onConfirm={async () => { if (del) await onDelete(del.id); setDel(null); }}
        onClose={() => setDel(null)}
      />
    </Modal>
  );
};

export default RecurringTasksModal;
